'use client';
import { LuvImage } from '@/components/ui';
import classNames from 'classnames';
import React, { FC } from 'react';
import styles from './ChildTab.module.scss';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';
import { GenInternalLink } from '@/components/generics';
import AddIcon from '@/components/ui/Icons/AddIcon';
import { Link } from '@/navigation';
import {
  buildRelativeInternalUrl,
  buildQueryString,
} from '@/utils/shopSubdomain';
import { TypeChildTabItem } from '@/components/account/ChildSelector/ChildSelector';

export interface ChildTabProps {
  childTabItem: TypeChildTabItem;
  isSelected: boolean;
  isDefault: boolean;
  editText: string;
  setSelectedChild: (newChildUuid: string) => void;
}

const TITLE_CHARACTER_COUNT = 16;

const ChildTab: FC<ChildTabProps> = ({
  childTabItem,
  isDefault,
  isSelected,
  editText,
  setSelectedChild,
}) => {
  const { title, id } = childTabItem;

  const handleClick = (cta: CTA_NAMES) => {
    // NoOp if already selected
    if (isSelected && cta == CTA_NAMES.CHILD_SELECTED) return;

    trackItemClickedEvent({
      url: window.location.href,
      type: CLICKED_ITEM_TYPES.LINK,
      pageLocation: PAGE_LOCATIONS.PLAY_KITS_PAGE,
      cta: cta,
    });

    if (!isDefault) {
      setSelectedChild(id);
    }
  };

  const imageStyle = {
    'border-[2px] bg-indigo-pastel border-blue-deep': isSelected,
    [`bg-indigo-pastel border-blue-deep ${styles.dashStyle}`]: isDefault,
    'border-[1px] hover:border-indigo-extra-dark hover:border-[2px] border-grey-dark':
      !isSelected && !isDefault,
    'mx-auto w-[88px] h-[88px] lg:w-28 lg:h-28 flex items-center rounded-full overflow-hidden relative z-10':
      true,
  };

  const TabImage = () => (
    <div className={classNames(imageStyle)}>
      {isDefault ? (
        <div className="mx-auto">
          <AddIcon />
        </div>
      ) : (
        <LuvImage
          imageData={childTabItem.icon}
          className={'w-11 lg:w-14 mx-auto'}
        />
      )}
    </div>
  );

  const _formatTitle = (title: string) =>
    title.length > TITLE_CHARACTER_COUNT
      ? title.slice(0, TITLE_CHARACTER_COUNT) + '...'
      : title;

  const titleStyle = {
    'mt-2 font-bold text-center text-base lg:text-xl': true,
    'text-blue-deep hover:text-blue-deep-hover': isDefault,
  };

  const TabTitle = () => (
    <p className={classNames(titleStyle)}>
      {isDefault ? title : _formatTitle(title)}
    </p>
  );

  function _generateEditChildLink(childUuid: string) {
    const queryString = buildQueryString([
      {
        key: 'page_source',
        value: 'subscriptions',
      },
    ]);
    return buildRelativeInternalUrl(
      ['account', 'v2', 'settings', 'edit-child', childUuid],
      queryString,
    );
  }

  const TabEditLink = () =>
    isSelected &&
    id && (
      <GenInternalLink
        classes="flex-col text-center font-bold text-blue-deep underline text-sm lg:text-base"
        cta={editText}
        href={_generateEditChildLink(id)}
        onClick={() => handleClick(CTA_NAMES.EDIT_CHILD)}
      />
    );

  const TabContent = () => (
    <>
      <TabImage />
      <TabTitle />
      <TabEditLink />
    </>
  );

  const ChildTab = () => (
    <button
      className={classNames({ 'cursor-default disabled': isSelected })}
      onClick={() => handleClick(CTA_NAMES.CHILD_SELECTED)}
    >
      <TabContent />
    </button>
  );

  function _addChildLink() {
    const queryString = buildQueryString([
      {
        key: 'page_source',
        value: 'subscriptions',
      },
    ]);
    return buildRelativeInternalUrl(
      ['account', 'v2', 'settings', 'add-a-child'],
      queryString,
    );
  }

  const AddChildTab = () => (
    <Link
      href={_addChildLink()}
      onClick={() => handleClick(CTA_NAMES.ADD_CHILD)}
    >
      <TabContent />
    </Link>
  );

  return (
    <div
      data-testid={isDefault ? 'add-child-tab' : 'child-tab'}
      className={classNames('flex flex-col content-center')}
    >
      {isDefault ? <AddChildTab /> : <ChildTab />}
    </div>
  );
};

export default React.memo(ChildTab);
